/**
 * Alegerea locatiei de ridicare, in fereastra de adresa.
 *
 * Fiecare locatie e un rand mare, apasabil, cu numele si adresa; harta se
 * deschide separat (link propriu), ca o atingere pe rand sa nu scoata omul din
 * aplicatie. Locatia aleasa poarta bifa si ramane prima la redeschidere.
 */

import type { PickupPointDto } from "@deliveryapp/shared";

import { pickupMapsUrl } from "./fulfillment";
import { IconCheck, IconChevronRight, IconStore } from "./icons";
import { Spinner } from "./ui";

export function PickupPicker({
  points,
  loading,
  selectedVendorId,
  onPick,
}: {
  points: readonly PickupPointDto[];
  loading: boolean;
  selectedVendorId: string | null;
  onPick: (point: PickupPointDto) => void;
}) {
  if (loading && points.length === 0) {
    return (
      <div className="flex items-center justify-center gap-2 py-8 text-sm text-ink-muted" role="status">
        <Spinner size={18} /> Se încarcă locațiile
      </div>
    );
  }
  if (points.length === 0) {
    return (
      <p className="rounded-card border border-line bg-surface px-4 py-6 text-center text-sm text-ink-muted">
        Momentan nicio locație nu primește comenzi de ridicare. Poți comanda cu livrare.
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-ink-muted">Vii tu după comandă. Fără taxă de livrare — îți arătăm ora la care e gata înainte să trimiți comanda.</p>
      <ul className="flex flex-col gap-2" aria-label="Locații de ridicare">
        {points.map((point) => {
          const selected = point.vendorId === selectedVendorId;
          return (
            <li
              key={point.vendorId}
              className={[
                "flex items-stretch overflow-hidden rounded-card border bg-canvas transition-colors",
                selected ? "border-brand bg-brand-soft" : "border-line hover:border-brand",
              ].join(" ")}
            >
              <button
                type="button"
                onClick={() => onPick(point)}
                aria-pressed={selected}
                className="da-tap flex min-w-0 flex-1 items-center gap-3 px-3.5 py-3 text-left"
              >
                <span
                  className={[
                    "grid h-10 w-10 shrink-0 place-items-center rounded-full",
                    selected ? "bg-brand text-brand-contrast" : "bg-surface text-brand-on-surface",
                  ].join(" ")}
                >
                  {selected ? <IconCheck size={20} /> : <IconStore size={20} />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-bold text-ink">{point.name}</span>
                  <span className="da-clamp-1 block text-xs text-ink-muted">{point.address}</span>
                </span>
                <IconChevronRight size={18} className="shrink-0 text-ink-muted" />
              </button>
              <a
                href={pickupMapsUrl(point)}
                target="_blank"
                rel="noreferrer"
                className="flex shrink-0 items-center border-l border-line px-3 text-xs font-semibold text-brand-on-surface hover:underline"
                aria-label={`Vezi pe hartă: ${point.name}`}
              >
                Hartă
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
